import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Strip markdown formatting so spoken text sounds natural.
 * Removes: links (keeps link text), bold/italic markers, list bullets, URLs.
 */
function stripMarkdown(text: string): string {
    return text
        // [link text](url) → link text
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        // Standalone URLs
        .replace(/https?:\/\/[^\s]+/g, '')
        // Bold / italic markers
        .replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1')
        // List bullets
        .replace(/^\s*[\-\*]\s+/gm, '')
        // Collapse whitespace
        .replace(/\s+/g, ' ')
        .trim();
}

export function useSpeechSynthesis() {
    const [isSpeaking, setIsSpeaking] = useState(false);
    const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

    // Cancel any ongoing speech on unmount
    useEffect(() => {
        return () => {
            window.speechSynthesis?.cancel();
        };
    }, []);

    // Keep isSpeaking in sync — poll because some browsers don't reliably fire 'end'
    useEffect(() => {
        if (!isSpeaking) return;
        const id = setInterval(() => {
            if (!window.speechSynthesis.speaking) {
                setIsSpeaking(false);
            }
        }, 250);
        return () => clearInterval(id);
    }, [isSpeaking]);

    const speak = useCallback((text: string) => {
        const synth = window.speechSynthesis;
        if (!synth) return;

        // Cancel any in-progress speech
        synth.cancel();

        const cleaned = stripMarkdown(text);
        if (!cleaned) return;

        const utterance = new SpeechSynthesisUtterance(cleaned);
        utterance.lang = 'de-DE';
        utterance.rate = 1;
        utterance.pitch = 1;

        // Try to pick a German voice (prefer one with "Google" or "female" for natural sound)
        const pickVoice = () => {
            const voices = synth.getVoices();
            const german = voices.filter(v => v.lang.startsWith('de'));
            if (german.length > 0) {
                // Prefer a "Google" or high-quality voice if available
                const preferred = german.find(v => /google/i.test(v.name));
                utterance.voice = preferred || german[0];
            }
        };

        // Voices may load asynchronously
        if (synth.getVoices().length > 0) {
            pickVoice();
        } else {
            synth.addEventListener('voiceschanged', pickVoice, { once: true });
        }

        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);

        utteranceRef.current = utterance;
        synth.speak(utterance);
        setIsSpeaking(true);
    }, []);

    const cancel = useCallback(() => {
        window.speechSynthesis?.cancel();
        setIsSpeaking(false);
    }, []);

    return { speak, cancel, isSpeaking };
}
